import React from "react";
import "./workouts.css";

import NeoLoader from "../../../components/Common/NeoLoader";
import { MasterDetailLayout, Breadcrumb, NeoEmptyState } from "../../../components/Common";
import { useWorkoutsPage } from "./hooks/useWorkoutsPage";

// Templates
import TemplateList from "./components/templates/TemplateList";
import TemplateEditor from "./components/templates/TemplateEditor";

// Prescriptions
import PrescriptionList from "./components/prescriptions/PrescriptionList";
import PrescriptionForm from "./components/prescriptions/PrescriptionForm";
import WeeklySessionsView from "./components/prescriptions/WeeklySessionsView";

// Modals
import AiWorkoutModal from "./components/ai/AiWorkoutModal";

const WorkoutsPage = () => {
    const {
        // Dados
        templates,
        prescriptions,
        canUseAi,
        aiWorkout,

        // UI State
        activeTab,
        selectedTemplate,
        selectedPrescription,
        prescribingTemplate,
        editingPrescription,
        filterMember,

        // Navigation
        handleSwitchToTemplates,
        handleSwitchToPrescriptions,

        // Template Actions
        handleSelectTemplate,
        handleCreateNewTemplate,
        handleSaveTemplate,
        handleDeleteTemplate,
        handleDuplicateTemplate,
        handleStartPrescription,

        // Prescription Actions
        handleDirectPrescription,
        handlePrescribe,
        handleSelectPrescription,
        handleUpdatePrescription,
        handleDeletePrescription,
        setFilterMember,
        setEditingPrescription,
        setSelectedPrescription,
        setPrescribingTemplate,

        // IA
        onAiGenerate,

        // Loading
        isLoading,
    } = useWorkoutsPage();

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <NeoLoader type="inline" loading={isLoading} />
            </div>
        );
    }

    // ── Render detail panel ──
    const renderDetail = () => {
        if (editingPrescription) {
            return (
                <PrescriptionForm
                    template={editingPrescription}
                    onPrescribe={handleUpdatePrescription}
                    isSaving={prescriptions.saving}
                    onCancel={() => {
                        setEditingPrescription(null);
                        setSelectedPrescription(null);
                    }}
                    onDelete={handleDeletePrescription}
                    isEditing={true}
                    userTemplates={templates.templates}
                />
            );
        }

        if (prescribingTemplate) {
            return (
                <PrescriptionForm
                    template={prescribingTemplate}
                    onPrescribe={handlePrescribe}
                    isSaving={prescriptions.saving}
                    onCancel={() => setPrescribingTemplate(null)}
                    userTemplates={templates.templates}
                    onAiGenerate={aiWorkout.toggleModal}
                />
            );
        }

        if (activeTab === "templates") {
            return (
                <TemplateEditor
                    initialValues={selectedTemplate}
                    onSave={handleSaveTemplate}
                    isSaving={templates.saving}
                    onDelete={handleDeleteTemplate}
                    onPrescribe={handleStartPrescription}
                    onAiGenerate={aiWorkout.toggleModal}
                />
            );
        }

        // Fallback — visão semanal quando membro está filtrado
        if (activeTab === "prescriptions" && filterMember) {
            return (
                <div className="p-4">
                    <div className="d-flex align-items-center mb-3">
                        <div className="rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: 40, height: 40 }}>
                            {filterMember.photo ? (
                                <img src={filterMember.photo} alt="" className="rounded-circle" style={{ width: 40, height: 40, objectFit: "cover" }} />
                            ) : (
                                <i className="mdi mdi-account text-primary font-size-20" />
                            )}
                        </div>
                        <div>
                            <h5 className="fw-bold text-dark mb-0">{filterMember.name}</h5>
                            <p className="text-muted font-size-12 mb-0">Visão Semanal de Treinos</p>
                        </div>
                    </div>
                    <WeeklySessionsView member={filterMember} />
                </div>
            );
        }

        return (
            <NeoEmptyState
                icon="mdi-account-clock-outline"
                title="Selecione um aluno"
                description="Busque um aluno na aba Prescrições para ver seus treinos da semana."
            />
        );
    };

    return (
        <React.Fragment>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <Breadcrumb title="Treinos" />
            </div>

            {/* ── Master-Detail Layout ── */}
            <div className="workouts-container">
                <MasterDetailLayout
                    master={
                        activeTab === "templates" ? (
                            <TemplateList
                                templates={templates.templates}
                                activeId={selectedTemplate?.id}
                                onSelect={handleSelectTemplate}
                                onCreateNew={handleCreateNewTemplate}
                                onDuplicate={handleDuplicateTemplate}
                                loading={templates.loading}
                                onAiGenerate={aiWorkout.toggleModal}
                                activeTab={activeTab}
                                onChangeTab={(tab) => {
                                    if (tab === "templates") handleSwitchToTemplates();
                                    else handleSwitchToPrescriptions();
                                }}
                                prescriptionsBadge={prescriptions.prescriptions.length}
                                templatesBadge={templates.templates.length}
                            />
                        ) : (
                            <PrescriptionList
                                prescriptions={prescriptions.prescriptions}
                                activeId={selectedPrescription?.id}
                                onSelect={handleSelectPrescription}
                                onDelete={handleDeletePrescription}
                                loading={prescriptions.loading}
                                filterMember={filterMember}
                                onFilterMember={setFilterMember}
                                onDirectPrescription={handleDirectPrescription}
                                activeTab={activeTab}
                                onChangeTab={(tab) => {
                                    if (tab === "templates") handleSwitchToTemplates();
                                    else handleSwitchToPrescriptions();
                                }}
                                prescriptionsBadge={prescriptions.prescriptions.length}
                                templatesBadge={templates.templates.length}
                            />
                        )
                    }
                    detail={renderDetail()}
                />
            </div>

            {/* Modal IA */}
            {canUseAi && (
                <AiWorkoutModal
                    isOpen={aiWorkout.isModalOpen}
                    toggle={aiWorkout.toggleModal}
                    formData={aiWorkout.formData}
                    updateFormField={aiWorkout.updateFormField}
                    toggleStyle={aiWorkout.toggleStyle}
                    availableProviders={aiWorkout.availableProviders}
                    loadingProviders={aiWorkout.loadingProviders}
                    isGenerating={aiWorkout.isGenerating}
                    error={aiWorkout.error}
                    onGenerate={onAiGenerate}
                />
            )}
        </React.Fragment>
    );
};

export default WorkoutsPage;
